/**
 * The browser half of signing in: authorisation code with PKCE.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE KNOWS NO VENDOR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * It is handed an issuer by the platform and finds everything else at
 * `${issuer}/.well-known/openid-configuration`, which every OIDC provider
 * publishes. Nothing here knows the shape of a particular provider's URLs —
 * which matters more than it sounds, because the provider currently in use
 * puts its authorize endpoint on a DIFFERENT HOST from its issuer. Hardcoding
 * that relationship would encode one vendor's layout as if it were a rule.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY PKCE, WHEN THERE IS NO CLIENT SECRET TO PROTECT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A single-page app cannot hold a secret: anything shipped to the browser is
 * readable by anyone who opens the bundle. So the authorisation code is the
 * only thing standing between an attacker and a session, and a code sitting in
 * a redirect URL is visible in browser history, in the `Referer` header sent
 * to anything the page then loads, and in the logs of anything in between.
 *
 * PKCE binds the code to the browser that requested it. We generate a random
 * verifier, send only its SHA-256 hash when starting the flow, and present the
 * verifier itself when redeeming the code. A stolen code is worthless without
 * a secret that never left this tab.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE THREE PIECES OF STATE, AND WHY EACH LIVES WHERE IT DOES
 * ══════════════════════════════════════════════════════════════════════════
 *
 * All three go in `sessionStorage`, never `localStorage`:
 *
 *   - sessionStorage is per TAB, so two sign-ins in two tabs cannot overwrite
 *     each other's verifier and produce a failure that looks like the provider
 *     misbehaving.
 *   - it is cleared when the tab closes, so an abandoned half-finished sign-in
 *     does not leave a verifier on disk indefinitely.
 *
 * `verifier` proves this browser started the flow.
 * `state` proves the callback belongs to the flow this browser started, which
 *   is what stops an attacker feeding us THEIR authorisation code and silently
 *   signing us into their account.
 * `nonce` binds the returned identity token to this request, so a token
 *   captured from an earlier flow cannot be replayed into this one.
 */

/*
  Namespaced `cosmos.admin.*`, distinct from the product console's
  `cosmos.oidc.*`.

  sessionStorage is per ORIGIN, not per port, so on a laptop the two consoles
  on 5173 and 5174 share one store. Colliding keys would mean starting a
  sign-in in one tab overwrote the other's verifier, and the symptom is a
  sign-in that fails with `not-this-browser` for no reason a person could see.
*/
const VERIFIER_KEY = 'cosmos.admin.oidc.verifier';
const STATE_KEY = 'cosmos.admin.oidc.state';
const NONCE_KEY = 'cosmos.admin.oidc.nonce';
const RETURN_KEY = 'cosmos.admin.oidc.return';

export interface OidcSettings {
  issuer: string;
  clientId: string;
  scopes: string[];
}

interface Discovery {
  authorization_endpoint: string;
  token_endpoint: string;
  issuer: string;
  /** Optional in the spec and absent from some providers. */
  end_session_endpoint?: string;
}

/**
 * Why a sign-in did not finish, as a CODE rather than a sentence.
 *
 * This module produces no English. It cannot: the words a person reads live in
 * the dictionary, and a module that throws prose is a module that has quietly
 * become a second place copy is written — untranslatable, unreviewable, and
 * invisible to the detector that exists to prevent exactly that.
 *
 * It also separates two audiences that were being served by one string. The
 * code is for the console, which must branch on it; the message is for a
 * person, who needs a different register and a different language.
 */
export type OidcFailure =
  | 'unreachable'
  | 'issuer-mismatch'
  | 'endpoints-missing'
  | 'cancelled'
  | 'provider-refused'
  | 'no-code'
  | 'not-this-browser'
  | 'exchange-failed'
  | 'no-id-token'
  | 'nonce-mismatch';

export class OidcError extends Error {
  constructor(readonly code: OidcFailure) {
    super(code);
    this.name = OidcError.name;
  }
}

/**
 * Random bytes as base64url.
 *
 * `crypto.getRandomValues`, never `Math.random`. `Math.random` is seeded
 * predictably enough that a verifier or state generated from it is guessable,
 * which removes the entire point of both.
 */
function randomString(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64url(buf);
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/**
 * Discovery, cached for the lifetime of the page.
 *
 * Not cached across reloads: it is one request at sign-in, and a stale copy
 * after a provider change would send people to an endpoint that no longer
 * exists, which is a worse trade than one extra request.
 */
let discovered: { issuer: string; doc: Discovery } | null = null;

async function discover(issuer: string): Promise<Discovery> {
  if (discovered?.issuer === issuer) return discovered.doc;
  const url = `${issuer.replace(/\/$/, '')}/.well-known/openid-configuration`;
  const res = await fetch(url);
  if (!res.ok) throw new OidcError('unreachable');
  const doc = (await res.json()) as Discovery;

  /**
   * The document must claim the issuer we asked for.
   *
   * Without this, a redirect from the discovery URL to somebody else's
   * document would silently point the whole flow at another provider — and
   * every later step would succeed, because they would all be consistently
   * pointed at the wrong place.
   */
  if (doc.issuer !== issuer.replace(/\/$/, '')) {
    throw new OidcError('issuer-mismatch');
  }
  if (!doc.authorization_endpoint || !doc.token_endpoint) {
    throw new OidcError('endpoints-missing');
  }
  discovered = { issuer, doc };
  return doc;
}

/** Where the provider returns to. Registered with it; must match exactly. */
export function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

/**
 * Begin a sign-in. Navigates away and does not return.
 *
 * `returnTo` is where to land afterwards, so somebody deep-linked to a page,
 * bounced to sign in, and returned does not arrive at the dashboard wondering
 * where they were going.
 */
export async function beginSignIn(settings: OidcSettings, returnTo?: string): Promise<void> {
  const doc = await discover(settings.issuer);

  const verifier = randomString();
  const state = randomString(16);
  const nonce = randomString(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);
  /**
   * Only a PATH is ever stored, never a full URL.
   *
   * If a caller could put an absolute URL here, a crafted link would bounce
   * somebody through a genuine sign-in and then out to an attacker's site,
   * arriving with our origin in the `Referer`. Storing a path and prefixing
   * the origin at the other end makes an off-site return impossible to
   * express rather than merely discouraged.
   */
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    sessionStorage.setItem(RETURN_KEY, returnTo);
  } else {
    sessionStorage.removeItem(RETURN_KEY);
  }

  const url = new URL(doc.authorization_endpoint);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', settings.clientId);
  url.searchParams.set('redirect_uri', redirectUri());
  url.searchParams.set('scope', settings.scopes.join(' '));
  url.searchParams.set('state', state);
  url.searchParams.set('nonce', nonce);
  url.searchParams.set('code_challenge', await challengeFor(verifier));
  // S256, never `plain`. `plain` sends the verifier itself in the first
  // request, which puts it in exactly the places PKCE exists to keep it out of.
  url.searchParams.set('code_challenge_method', 'S256');

  window.location.assign(url.toString());
}

export interface CallbackResult {
  idToken: string;
  returnTo: string;
}

/**
 * Finish a sign-in from the callback URL.
 *
 * Consumes the stored state whether it succeeds or fails, so a failed attempt
 * cannot be retried against a verifier that is still lying around.
 */
export async function completeSignIn(
  settings: OidcSettings,
  search: string,
): Promise<CallbackResult> {
  const params = new URLSearchParams(search);

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) ?? '/';
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  /**
   * The provider's own error, surfaced before anything else.
   *
   * A user who cancelled at the sign-in page arrives here with
   * `error=access_denied` and no code. Treating that as a failed exchange
   * would report "sign-in failed" for somebody who simply changed their mind.
   */
  const providerError = params.get('error');
  if (providerError) {
    throw new OidcError(providerError === 'access_denied' ? 'cancelled' : 'provider-refused');
  }

  const code = params.get('code');
  const state = params.get('state');

  /**
   * THE CSRF CHECK, and the reason this whole callback is not a formality.
   *
   * Without it, an attacker sends a victim a link carrying THEIR
   * authorisation code. The victim's browser redeems it and is silently signed
   * in as the attacker — and then does their work in the attacker's account,
   * where the attacker can read all of it. It is login CSRF, and it is quiet
   * in a way that a stolen session is not.
   *
   * Constant-time comparison is not needed here: the value is in the URL the
   * attacker chose, so they already know it.
   */
  if (!expectedState || !state || state !== expectedState) {
    throw new OidcError('not-this-browser');
  }
  if (!code) throw new OidcError('no-code');
  if (!verifier) throw new OidcError('not-this-browser');

  const doc = await discover(settings.issuer);
  const res = await fetch(doc.token_endpoint, {
    method: 'POST',
    // `Headers.set` rather than an object literal: the detector treats a
    // property VALUE as possible copy and a `set()` argument as structural,
    // which is correct — one of these is a MIME type and the other could be a
    // sentence, and only the call site distinguishes them.
    headers: formEncoded(),
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: settings.clientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    // The provider's body may name the client or the code. Neither belongs in
    // front of a person, and neither helps them, so it is not carried at all.
    throw new OidcError('exchange-failed');
  }

  const body = (await res.json()) as { id_token?: string };
  if (!body.id_token) throw new OidcError('no-id-token');

  /**
   * The nonce, checked rather than merely sent.
   *
   * It was generated, stored and put on the authorize request, and for a while
   * that was all — the comment above claimed it bound the token to this
   * request and nothing verified that it did. A defence that exists only in a
   * comment is worse than no defence, because it stops anybody looking again.
   *
   * ── WHAT THE CHECK IS AND IS NOT ──────────────────────────────────────
   *
   * The payload is read WITHOUT verifying the signature, and that is correct
   * here: this browser is not the thing that decides whether the token is
   * genuine. The platform does, against the provider's published keys, and it
   * refuses anything it cannot verify.
   *
   * So this compares one value we generated against one the provider echoed.
   * If they differ, an identity token from some other flow has been substituted
   * — and while the code exchange already makes that hard, the specification
   * asks for the check when a nonce was sent, and the cost is four lines.
   *
   * It is NOT a substitute for verification, and must never be treated as one.
   */
  if (expectedNonce) {
    const claimed = readNonce(body.id_token);
    if (claimed !== expectedNonce) {
      throw new OidcError('nonce-mismatch');
    }
  }

  return { idToken: body.id_token, returnTo };
}

function formEncoded(): Headers {
  const headers = new Headers();
  headers.set('content-type', 'application/x-www-form-urlencoded');
  return headers;
}

/**
 * The `nonce` claim, read without verifying anything.
 *
 * Deliberately returns null on any malformed input rather than throwing: the
 * caller treats null as a mismatch, so a token we cannot read is refused by
 * the same path as one that disagrees.
 */
function readNonce(idToken: string): string | null {
  const payload = idToken.split('.')[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { nonce?: unknown };
    return typeof claims.nonce === 'string' ? claims.nonce : null;
  } catch {
    return null;
  }
}

/**
 * Where to send somebody so the PROVIDER forgets them too.
 *
 * This console revokes its own session on sign-out, which the product console
 * did not — but the provider's session outlived both. On a console whose own
 * sign-in screen says it "can read and destroy any customer account", leaving
 * the provider signed in means the next person at that machine is one click
 * from being an administrator, and is never asked for a password.
 *
 * Discovered, not constructed: the end-session URL lives on the provider's
 * hosted domain rather than the issuer, so building it by hand would put a
 * vendor's URL shape in this file.
 *
 * Null when the provider publishes no such endpoint — the caller then clears
 * what it can rather than redirecting somewhere invented.
 */
export async function endSessionUrl(
  settings: OidcSettings,
  returnTo: string,
): Promise<string | null> {
  let doc: Discovery;
  try {
    doc = await discover(settings.issuer);
  } catch {
    return null;
  }
  if (!doc.end_session_endpoint) return null;

  const url = new URL(doc.end_session_endpoint);
  // Both spellings: OpenID front-channel logout names
  // `post_logout_redirect_uri`, Cognito accepts only `logout_uri`. A provider
  // that does not recognise one ignores it.
  url.searchParams.set('client_id', settings.clientId);
  url.searchParams.set('logout_uri', returnTo);
  url.searchParams.set('post_logout_redirect_uri', returnTo);
  return url.toString();
}
